/*
 * Every API path the desk calls, in one place. Nothing outside this file
 * spells out a URL: add the endpoint here first, then call `api(endpoints.x)`.
 *
 * Paths are relative to API_ROOT. When the server grows a versioned prefix,
 * bump API_VERSION and every call moves with it.
 *
 * Mirrors the routers in backend/src/app.ts and backend/src/routes/*.
 */

/** "" while the server is unversioned; "v1" once it serves /api/v1. */
export const API_VERSION = "";
export const API_ROOT = API_VERSION ? `/api/${API_VERSION}` : "/api";

const seg = (v: string) => encodeURIComponent(v);

/** Booking state changes, each a POST on /bookings/:ref/<action>. */
export type BookingAction = "confirm" | "extend-hold" | "check-in" | "check-out" | "mark-paid" | "refund" | "acknowledge" | "cancel";

export const endpoints = {
  /** The staff app's own sign-in (phone + PIN). */
  staffAuth: {
    signIn: "/auth/staff/sign-in",
    signOut: "/auth/staff/sign-out",
    device: "/auth/staff/device",
    join: (token: string) => `/auth/staff/join/${seg(token)}`,
    preview: "/auth/staff/preview",
  },

  /** The staff app. Every answer is staff-safe on the server. */
  staff: {
    me: "/staff/me",
    home: "/staff/home",
    changePin: "/staff/change-pin",
    checks: "/staff/checks",
    check: (id: string) => `/staff/checks/${seg(id)}`,
    take: (id: string) => `/staff/checks/${seg(id)}/take`,
    progress: (id: string) => `/staff/checks/${seg(id)}/progress`,
    flag: (id: string) => `/staff/checks/${seg(id)}/flag`,
    unflag: (id: string) => `/staff/checks/${seg(id)}/unflag`,
    checkPhotos: (id: string) => `/staff/checks/${seg(id)}/photos`,
    submit: (id: string) => `/staff/checks/${seg(id)}/submit`,
    places: "/staff/places",
    repairs: "/staff/repairs",
    repair: (id: string) => `/staff/repairs/${seg(id)}`,
    repairStatus: (id: string) => `/staff/repairs/${seg(id)}/status`,
    repairPhotos: (id: string) => `/staff/repairs/${seg(id)}/photos`,
    laundry: "/staff/laundry",
    batches: "/staff/laundry/batches",
    batch: (id: string) => `/staff/laundry/batches/${seg(id)}`,
    receive: (id: string) => `/staff/laundry/batches/${seg(id)}/receive`,
    keys: "/staff/keys",
    reveal: (roomId: string) => `/staff/keys/${seg(roomId)}/reveal`,
    code: (roomId: string) => `/staff/keys/${seg(roomId)}/code`,
    lost: "/staff/lost",
    lostPhotos: (id: string) => `/staff/lost/${seg(id)}/photos`,
    returned: (id: string) => `/staff/lost/${seg(id)}/returned`,
    file: (id: string) => `/staff/files/${seg(id)}`,
  },

  auth: {
    me: "/auth/me",
    signIn: "/auth/sign-in",
    signOut: "/auth/sign-out",
    twoFactor: "/auth/two-factor",
    twoFactorSetup: "/auth/two-factor/setup",
    twoFactorEnable: "/auth/two-factor/enable",
    twoFactorDisable: "/auth/two-factor/disable",
    forgot: "/auth/forgot",
    reset: "/auth/reset",
    invite: "/auth/invite",
    acceptInvite: "/auth/accept-invite",
    password: "/auth/password",
    passwordStrength: "/auth/password-strength",
  },

  bookings: {
    list: "/bookings",
    create: "/bookings",
    detail: (ref: string) => `/bookings/${seg(ref)}`,
    history: (ref: string) => `/bookings/${seg(ref)}/history`,
    action: (ref: string, action: BookingAction) => `/bookings/${seg(ref)}/${action}`,
  },

  /** Liveness, and the API's version for the footer. */
  health: "/health",
  dashboard: "/dashboard",
  calendar: "/calendar",
  availability: "/availability",
  team: "/team",
  activity: "/activity",
  paymentMethods: "/payment-methods",
  paymentMethod: (code: string) => `/payment-methods/${seg(code)}`,
  paymentMethodAccepted: "/payment-methods/accepted",
  activityAreas: "/activity/areas",
  activityExport: "/activity/export",

  conflicts: {
    list: "/conflicts",
    count: "/conflicts/count",
    detail: (id: string) => `/conflicts/${seg(id)}`,
    resolve: (id: string) => `/conflicts/${seg(id)}/resolve`,
  },

  guests: {
    list: "/guests",
    lookup: "/guests/lookup",
    detail: (id: string) => `/guests/${seg(id)}`,
    restore: (id: string) => `/guests/${seg(id)}/restore`,
  },

  rates: "/rates",
  mealPlans: "/meal-plans",
  /** PUT edits one; PUT on the bare path adds. */
  mealPlan: (id: string) => `/meal-plans/${seg(id)}`,
  addons: "/addons",
  addon: (id: string) => `/addons/${seg(id)}`,

  locations: {
    list: "/locations",
    detail: (id: string) => `/locations/${seg(id)}`,
  },

  properties: {
    list: "/properties",
    detail: (id: string) => `/properties/${seg(id)}`,
    inventory: (id: string) => `/properties/${seg(id)}/inventory`,
  },

  roomTypes: {
    list: "/room-types",
    detail: (id: string) => `/room-types/${seg(id)}`,
  },

  rooms: {
    list: "/rooms",
    /** Several rooms of one type in one request. */
    bulk: "/rooms/bulk",
    detail: (id: string) => `/rooms/${seg(id)}`,
    beds: (id: string) => `/rooms/${seg(id)}/beds`,
  },

  buildings: {
    list: "/buildings",
    detail: (id: string) => `/buildings/${seg(id)}`,
  },

  settings: "/settings",
  access: {
    list: "/settings/access",
    room: (roomId: string) => `/settings/access/${seg(roomId)}`,
    code: (roomId: string) => `/settings/access/${seg(roomId)}/code`,
    reveal: (roomId: string) => `/settings/access/${seg(roomId)}/reveal`,
    history: (roomId: string) => `/settings/access/${seg(roomId)}/history`,
  },

  /** Room checks, the lockbox gate, laundry and per-room checklists. */
  upkeep: {
    count: "/upkeep/count",
    /** The Plan's Phone view: who can be previewed, and the iframe's one-time code. */
    preview: "/upkeep/preview",
    previewPeople: "/upkeep/preview/people",
    plan: "/upkeep/plan",
    mine: "/upkeep/mine",
    rules: "/upkeep/rules",
    room: (roomId: string) => `/upkeep/rooms/${seg(roomId)}`,
    roomSetup: (roomId: string) => `/upkeep/rooms/${seg(roomId)}/setup`,
    fill: "/upkeep/tasks/fill",
    roomDay: (roomId: string) => `/upkeep/rooms/${seg(roomId)}/day`,
    roomHistory: (roomId: string) => `/upkeep/rooms/${seg(roomId)}/history`,
    tasks: "/upkeep/tasks",
    tasksBulk: "/upkeep/tasks/bulk",
    task: (id: string) => `/upkeep/tasks/${seg(id)}`,
    taskAction: (id: string, action: "progress" | "assign" | "submit" | "override" | "send-back" | "undo-send-back" | "move" | "comment" | "detail" | "cancel") => `/upkeep/tasks/${seg(id)}/${action}`,
    finding: (id: string, action: "charge" | "repair" | "restock") => `/upkeep/findings/${seg(id)}/${action}`,
    lockboxSent: (bookingId: string) => `/upkeep/lockbox/${seg(bookingId)}/sent`,
    laundry: "/upkeep/laundry",
    batches: "/upkeep/laundry/batches",
    batch: (id: string) => `/upkeep/laundry/batches/${seg(id)}`,
    receive: (id: string) => `/upkeep/laundry/batches/${seg(id)}/receive`,
    stock: "/upkeep/laundry/stock",
    linenTypes: "/upkeep/laundry/linen-types",
    vendors: "/upkeep/laundry/vendors",
    checklists: "/upkeep/checklists",
    checklistTypes: "/upkeep/checklists/types",
    starters: "/upkeep/checklists/starters",
    checklistItems: (roomTypeId: string) => `/upkeep/checklists/${seg(roomTypeId)}/items`,
    checklistSections: (roomTypeId: string) => `/upkeep/checklists/${seg(roomTypeId)}/sections`,
    checklistSection: (roomTypeId: string, sectionId: string) => `/upkeep/checklists/${seg(roomTypeId)}/sections/${seg(sectionId)}`,
    replaceList: (roomTypeId: string) => `/upkeep/checklists/${seg(roomTypeId)}/replace`,
    checklistItem: (id: string) => `/upkeep/checklists/items/${seg(id)}`,
    hideItem: (id: string) => `/upkeep/checklists/items/${seg(id)}/hide`,
    section: (id: string) => `/upkeep/checklists/sections/${seg(id)}`,
  },

  /** Where bookings come from: the built-in sources and any the business added. */
  channels: {
    list: "/channels",
    detail: (code: string) => `/channels/${seg(code)}`,
  },

  /** Aiosell: the codes a stay pushes its availability under. */
  channelManager: {
    detail: "/channel-manager",
    sync: "/channel-manager/sync",
  },

  taxes: {
    list: "/taxes",
    detail: (id: string) => `/taxes/${seg(id)}`,
    /** Add or align one tax at every property in scope. */
    everywhere: "/taxes/apply-everywhere",
  },

  /** Repairs. `open` is the board; the bare list is closed issues, a page at a time. */
  issues: {
    open: "/issues/open",
    closed: "/issues",
    create: "/issues",
    counts: "/issues/counts",
    detail: (id: string) => `/issues/${seg(id)}`,
    schedule: (id: string) => `/issues/${seg(id)}/schedule`,
    status: (id: string) => `/issues/${seg(id)}/status`,
    notes: (id: string) => `/issues/${seg(id)}/notes`,
    offSale: (id: string) => `/issues/${seg(id)}/off-sale`,
    onSale: (id: string) => `/issues/${seg(id)}/on-sale`,
    files: (id: string) => `/issues/${seg(id)}/files`,
  },

  /** Money paid out, and paying people back. */
  spends: {
    list: "/spends",
    create: "/spends",
    summary: "/spends/summary",
    suppliers: "/spends/suppliers",
    settle: "/spends/settle",
    detail: (id: string) => `/spends/${seg(id)}`,
    void: (id: string) => `/spends/${seg(id)}/void`,
    reimburse: (id: string) => `/spends/${seg(id)}/reimburse`,
    reject: (id: string) => `/spends/${seg(id)}/reject`,
    files: (id: string) => `/spends/${seg(id)}/files`,
  },

  /** Routine checks that raise a repair when they come due. */
  plans: {
    list: "/plans",
    detail: (id: string) => `/plans/${seg(id)}`,
  },

  /** Notes for the next shift. */
  handover: {
    list: "/handover",
    done: (id: string) => `/handover/${seg(id)}/done`,
    detail: (id: string) => `/handover/${seg(id)}`,
  },

  lost: {
    list: "/lost",
    detail: (id: string) => `/lost/${seg(id)}`,
    resolve: (id: string) => `/lost/${seg(id)}/resolve`,
    contact: (id: string) => `/lost/${seg(id)}/contact`,
    match: "/lost/match",
    files: (id: string) => `/lost/${seg(id)}/files`,
  },

  /** Photos and receipts; a FileDTO's `url` already points here. */
  files: {
    detail: (id: string) => `/files/${seg(id)}`,
  },

  /** Owner only. */
  reports: {
    performance: "/reports/performance",
    channels: "/reports/channels",
    cancellations: "/reports/cancellations",
  },

  users: {
    list: "/users",
    detail: (id: string) => `/users/${seg(id)}`,
    resendInvite: (id: string) => `/users/${seg(id)}/resend-invite`,
    /* Staff: phone + PIN, one property, abilities one by one. */
    inviteStaff: "/users/staff",
    access: (id: string) => `/users/${seg(id)}/access`,
    property: (id: string) => `/users/${seg(id)}/property`,
    grants: (id: string) => `/users/${seg(id)}/grants`,
    preset: (id: string) => `/users/${seg(id)}/grants/preset`,
    pinReset: (id: string) => `/users/${seg(id)}/pin-reset`,
    pinLink: (id: string) => `/users/${seg(id)}/pin-link`,
    signOutEverywhere: (id: string) => `/users/${seg(id)}/sign-out-everywhere`,
  },
} as const;
